/* eslint-disable @linkhr/linkhr/no-hex-colors -- brand letterform + gradient literals: the lockup never themes or follows the tenant primary */
/**
 * The "LinkHR" wordmark: "Link" + "R" in `currentColor`, the "H" in the teal →
 * ink gradient. Sign-in header (ink) and the photo canvas (white).
 *
 * A local copy of the design-system component: `@linkhr/auth` must not depend
 * on the design system, so the auth shell carries its own lockups (same path
 * data as `packages/design-system/src/brand/`).
 */
import { useId, type JSX } from "react";

export interface LinkHrWordmarkProps {
  /** Rendered height in px (width follows the 107:30 ratio). */
  height?: number;
  /**
   * `auto` (default) follows the theme's brand ink (ink in light, near-white
   * in dark); `light` forces ink and `dark` forces white for chrome that stays
   * one color in both themes (the sign-in photo canvas). `var(--color-brand-ink)`
   * is a plain CSS variable read, not an import — the no-design-system-dependency
   * boundary for `@linkhr/auth` holds.
   */
  tone?: "auto" | "light" | "dark";
  className?: string;
}

export function LinkHrWordmark({
  height = 30,
  tone = "auto",
  className,
}: LinkHrWordmarkProps): JSX.Element {
  const id = useId().replace(/:/g, "");
  return (
    <svg
      width={Math.round((height * 107) / 30)}
      height={height}
      viewBox="0 0 107 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      // Brand letterform colors, not UI tokens: ink on light chrome, white on
      // dark chrome, in every theme and for every tenant primary.
      style={{
        color: tone === "auto" ? "var(--color-brand-ink)" : tone === "dark" ? "#F6F8F9" : "#16151C",
      }}
      {...(className !== undefined ? { className } : {})}
    >
      <path
        d="M17.9628 5.44525C18.7855 5.58026 19.6679 6.15029 20.4157 6.88533C21.4926 7.96538 22.0909 9.43546 22.1059 10.9655C22.1358 18.1209 22.1657 21.5861 22.1956 29.1765C22.1208 29.2515 22.0909 29.2815 22.0161 29.3565C20.341 29.3565 18.6658 29.3415 16.9757 29.3265C16.9458 19.876 16.9159 15.0457 16.871 5.61026L16.9757 5.56526C17.2898 5.43025 17.6188 5.38525 17.9479 5.44525H17.9628Z"
        fill="currentColor"
      />
      <path
        d="M0 0C2.28837 0.0900045 4.99553 1.9201 5.23484 4.35022C5.21989 6.90035 5.23484 9.45048 5.23484 12.0006C5.23484 14.5507 5.23484 14.6407 5.23484 15.9458C5.23484 17.2959 5.0404 18.871 5.5938 20.146C6.11729 21.3461 7.35869 22.4561 8.45053 23.1312C10.4398 24.3612 12.6234 23.8212 14.3285 26.1163C14.852 26.8214 15.2558 28.7865 15.1362 29.3415C13.805 29.2065 8.63001 29.8215 5.38441 28.0514C2.36316 26.4013 0.478614 23.2212 0.059827 19.846C0.0448703 19.681 0.014957 19.051 0.014957 18.901C0.014957 12.7956 -0.0149563 0.0300015 0.014957 0H0Z"
        fill="currentColor"
      />
      <path
        d="M41.5345 4.00519V11.2356C38.5582 8.71543 33.8169 9.39047 31.4687 12.4356C31.3939 12.5256 31.3341 12.6306 31.2593 12.7356C30.5713 13.7857 30.2273 15.0157 30.2273 16.2608V23.6262C30.2273 24.3012 30.0927 24.9763 29.8085 25.6063C29.6589 25.9363 29.4944 26.2513 29.3748 26.4463C28.2829 28.1264 25.9945 29.3565 23.9904 29.3415C23.9604 29.3115 23.9455 29.2965 23.9156 29.2665L24.0502 15.9008C24.0801 12.9756 25.0523 10.1255 26.862 7.84539C30.1824 3.66017 35.7912 1.83008 41.4448 4.02019H41.5345V4.00519Z"
        fill={`url(#${id}-g0)`}
      />
      <path
        d="M59.0492 29.3415C58.7501 29.3265 58.3014 29.3415 57.763 29.3415C55.5942 29.3715 52.6627 29.4315 50.1799 28.0664C48.8936 27.3614 47.7869 26.3563 46.9194 25.1563C46.9194 26.5213 46.9194 28.0364 46.9343 29.3565H41.5948C41.5499 23.3112 41.505 17.2659 41.4751 11.2356V4.0052C41.4602 2.79014 41.4452 1.23006 41.4303 0C41.4303 0 41.9388 0 42.0435 0C43.3896 0.0300015 44.9301 1.17006 45.9023 2.50513C46.4707 3.28517 46.7698 4.21521 46.7997 5.17526C46.8147 5.9103 46.8296 6.66034 46.8595 7.39537C46.8595 7.38037 46.8895 8.44543 46.8895 10.0505C49.4171 7.54538 51.8401 5.59528 54.3828 3.04515C56.013 3.76519 56.3122 7.00535 55.4746 8.25042C53.949 9.8705 52.4234 11.4906 50.8978 13.0957C49.1629 14.9258 48.759 17.6709 49.9406 19.906C49.9556 19.951 49.9855 19.981 50.0004 20.026C50.0004 20.026 50.2547 20.491 50.2697 20.536C50.2697 20.551 51.0923 21.8861 51.541 22.3211C52.1542 22.9212 52.8572 23.4462 53.605 23.8962C54.4127 24.3912 55.2802 24.7813 56.1327 25.1863C56.8955 25.5463 57.6583 25.8913 58.2266 26.5363C58.436 26.7614 58.5856 27.0314 58.6753 27.3314C58.8997 28.0514 59.0044 28.8915 59.0343 29.3415H59.0492Z"
        fill="currentColor"
      />
      <path
        d="M66.976 2.97014V9.76548C66.976 11.1756 68.1127 12.3156 69.5186 12.3156H79.3003C80.7212 12.3156 81.8729 11.1605 81.8729 9.73548V9.48046C81.8729 8.05539 83.0245 6.90033 84.4454 6.90033C85.8663 6.90033 87.018 8.05539 87.018 9.48046V26.7763C87.018 28.2014 85.8663 29.3565 84.4454 29.3565C83.0245 29.3565 81.8729 28.2014 81.8729 26.7763V19.996C81.8729 18.5709 80.7212 17.4159 79.3003 17.4159H69.5186C68.1127 17.4159 66.976 18.5559 66.976 19.966V28.4714C66.976 29.8815 67.305 29.6115 64.4334 29.6115C63.0274 29.6115 61.8907 28.4714 61.8907 27.0614V0.405007H64.4334C65.8393 0.405007 66.976 1.54506 66.976 2.95514V2.97014Z"
        fill={`url(#${id}-g1)`}
      />
      <path
        d="M97.6221 0.300003C104.188 0.300003 106.985 4.36521 106.985 9.04544C106.985 13.7257 105.848 15.1208 103.036 16.8758C101.9 17.5809 101.496 19.0209 102.169 20.176L106.536 25.8463C107.479 27.4814 106.312 29.5365 104.412 29.5365C102.513 29.5365 102.737 29.0715 102.304 28.3214L95.9919 19.441C95.5581 18.6909 94.7505 18.2259 93.883 18.2259H87.6759C87.1973 18.2259 86.9431 18.4359 86.9431 18.9609V26.7913C86.9431 28.1414 85.8512 29.2515 84.4902 29.2515H84.2957C82.9496 29.2515 81.8428 28.1564 81.8428 26.7913V17.9709C81.8428 14.6857 83.2188 13.3357 86.4196 13.3357H97.7717C100.658 13.3357 101.825 11.3406 101.825 9.27046C101.825 7.20035 100.569 5.41526 97.7717 5.41526H92.163C90.8169 5.41526 89.7101 4.32021 89.7101 2.95514V2.76013C89.7101 1.41006 90.8019 0.300003 92.163 0.300003H97.6072"
        fill="currentColor"
      />
      <path
        d="M97.622 0.300003H92.1777C90.8316 0.300003 89.7248 1.39506 89.7248 2.76013V2.95514C89.7248 4.3052 90.8167 5.41526 92.1777 5.41526H97.7865C100.598 5.41526 101.84 7.18535 101.84 9.27046C101.84 11.3556 100.673 13.3357 97.7865 13.3357H86.4344C83.2336 13.3357 81.8576 14.6707 81.8576 17.9709V27.0764C81.8576 28.4264 82.9494 29.5365 84.3105 29.5365H84.5049C85.163 29.5365 86.3147 29.5065 87.0625 29.5065C87.0625 29.5065 87.0177 27.8414 87.0177 27.1514L86.9578 18.9759C86.9578 18.4509 87.2121 18.2409 87.6907 18.2409H93.8977C94.7652 18.2409 95.5729 18.7059 96.0066 19.456L102.318 28.3364C102.752 29.0865 103.56 29.5515 104.427 29.5515C105.863 29.5515 106.88 28.3664 106.88 27.0914C106.88 26.6863 106.775 26.2663 106.551 25.8613L102.184 20.191C101.959 19.816 101.855 19.396 101.855 18.9909C101.855 18.1659 102.288 17.3559 103.051 16.8758C105.863 15.1358 107 12.0006 107 9.04544C107 4.36521 104.188 0.300003 97.6369 0.300003"
        fill="currentColor"
      />
      <path
        d="M84.4155 5.32528C85.8065 5.38528 86.9432 4.33523 87.018 2.91016C87.0778 1.54509 86.0009 0.375028 84.6099 0.300024C83.2638 0.22502 82.0823 1.33508 82.0224 2.73015C81.9626 4.14022 83.0096 5.26527 84.4305 5.32528H84.4155Z"
        fill={`url(#${id}-g2)`}
      />
      <defs>
        <linearGradient
          id={`${id}-g0`}
          x1="23.9006"
          y1="16.2158"
          x2="41.5345"
          y2="16.2158"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6CCDE0" />
          <stop offset="0.1" stopColor="#63BCCE" />
          <stop offset="0.3" stopColor="#4C919F" />
          <stop offset="0.7" stopColor="#284C53" />
          <stop offset="1" />
        </linearGradient>
        <linearGradient
          id={`${id}-g1`}
          x1="61.8758"
          y1="15.0307"
          x2="87.003"
          y2="15.0307"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6CCDE0" />
          <stop offset="0.1" stopColor="#63BCCE" />
          <stop offset="0.3" stopColor="#4C919F" />
          <stop offset="0.7" stopColor="#284C53" />
          <stop offset="1" />
        </linearGradient>
        <linearGradient
          id={`${id}-g2`}
          x1="82.0075"
          y1="2.82015"
          x2="87.003"
          y2="2.82015"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#6CCDE0" />
          <stop offset="0.1" stopColor="#63BCCE" />
          <stop offset="0.3" stopColor="#4C919F" />
          <stop offset="0.7" stopColor="#284C53" />
          <stop offset="1" />
        </linearGradient>
      </defs>
    </svg>
  );
}
